// @ts-nocheck

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { StickyMatchActionsRowContexts, type StickyMatchActionsRowContext } from '../../constants/match';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getAppDOMElement } from '../../DOMElements';
import useMatchViewContext from '../../hooks/useMatchViewContext';
import theme from '../../styled-components/dark';
import { ZIndices } from '../../styled-components/dark/common';
import { type MatchBetFilter } from '../../utils/contexts';
import { FilterWrapperListClassName } from '../FiltersWrapper';
import MatchBetFiltersHandler from '../MatchBetFiltersHandler';
import MatchViewModeSwitchContainer from '../MatchViewModeSwitch/MatchViewModeSwitchContainer';

const Placeholder = styled.div`
  height: ${({ $height }) => $height}px;
`;

const Wrapper = styled.div`
  position: sticky;
  overflow: hidden;
  z-index: ${ZIndices.matchBetFilters};

  transition: none 300ms ease-in-out;
  transition-property: transform, background, border-radius, opacity;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  ${({ $offsetTop, $isSticky }) => css`
    position: ${Target.__MOBILE__ || !$isSticky ? 'sticky' : 'fixed'};
    top: ${$offsetTop - 1}px;
    font-size: ${Target.__MOBILE__ ? 1 : 0.9}em;
    background: var(--match-bet-filters-main-background, ${Target.__MOBILE__ || $isSticky ? '#000' : 'none'});

    ${$isSticky
      ? css`
          padding: 1em 0.6em 0;
        `
      : css`
          padding: 0em 0.6em 0;
        `}
  `}
`;

const StyledMatchViewModeSwitch = styled(MatchViewModeSwitchContainer)`
  transform: translateY(-0.3em);
  margin-top: 0.7em;
  margin-right: 0em;

  ${({ $isFromLineUp }) =>
    $isFromLineUp
      ? css`
          --MatchViewModeSwitch-backgroundColor: rgba(255, 255, 255, 0.1);
        `
      : undefined}
`;

const StyledMatchBetFiltersHandlerContainer = styled(MatchBetFiltersHandler)`
  ${({ $isSticky }) => css`
    ${$isSticky
      ? css`
          padding: 0.3em 0;
        `
      : css`
          padding: 0;
        `}
  `}

  ${({ $marginList }) =>
    $marginList
      ? css`
          & .${FilterWrapperListClassName} {
            & > * {
              &:first-child {
                margin-left: 1em;
              }
            }
          }
        `
      : undefined}


  ${!Target.__MOBILE__
    ? css`
        position: sticky;
      `
    : undefined}

  ${({ $isMyMatchAvailable }) =>
    $isMyMatchAvailable
      ? css`
          --BScrollViewLeftShadow-backgroundColor: var(--BScrollViewShadow-backgroundColor, black);
          --FiltersWrapper-marginLeft: 0.5em;
        `
      : undefined}
`;

type StickyMatchActionsRowProps = {
  className?: string;
  filters: Array<MatchBetFilter>;
  onFilterChanged: (filter: string | null) => void;
  defaultFilterId?: string;
  untoggleable?: boolean;
  noAutoScroll?: boolean;
  context?: StickyMatchActionsRowContext;
  isMyMatchAvailable: boolean;
};

const StickyMatchActionsRow = ({
  className = '',
  filters,
  onFilterChanged,
  defaultFilterId,
  untoggleable = false,
  noAutoScroll = false,
  context = '',
  isMyMatchAvailable,
}: StickyMatchActionsRowProps) => {
  const placeholderRef = useRef();
  const wrapperRef = useRef();
  const { safeAreaInset } = useViewportContext();

  const location = useLocation();

  const scrollElementRef = useRef(Target.__MOBILE__ ? getAppDOMElement() : window);
  const initialWrapperPosition = useRef(0);
  const scrollPosition = useRef(0);
  const wrapPosition = useRef(0);

  const [isSticky, setIsSticky] = useState(false);

  const { isWidgetSticky, widgetSize, stickyHeaderSize, promoSize } = useMatchViewContext();

  const fixedContentSize = useMemo(
    () => (isWidgetSticky ? widgetSize : stickyHeaderSize),
    [isWidgetSticky, stickyHeaderSize, widgetSize],
  );

  const measuredWrapperHeight = useCallback((node) => {
    wrapperRef.current = node;
    const scrollElement = scrollElementRef.current;

    if (node && scrollElement) {
      const { bottom } = node.getBoundingClientRect();
      const scrollY = scrollElementRef.current.scrollTop ?? scrollElementRef.current.scrollY;

      initialWrapperPosition.current = bottom + scrollY;
    }
  }, []);

  useLayoutEffect(() => {
    const wrapperNode = wrapperRef.current;
    const placeholderNode = placeholderRef.current;

    if (wrapperNode) {
      const { height: wrapperNodeHeight } = wrapperNode.getBoundingClientRect() ?? {};
      const { parentNode: wrapperParentNode } = wrapperNode;
      const { width: wrapperParentNodeWidth } = wrapperParentNode?.getBoundingClientRect() ?? {};

      wrapperNode.style.maxWidth = (() => {
        if (Target.__MOBILE__) {
          return '100%';
        }
        if (typeof wrapperParentNodeWidth === 'number') {
          return `${wrapperParentNodeWidth}px`;
        }
      })();

      if (placeholderNode && typeof wrapperNodeHeight === 'number') {
        placeholderNode.style.height = `${wrapperNodeHeight}px`;
      }
    }
  }, []);

  useLayoutEffect(() => {
    const wrapperNode = wrapperRef.current;
    const scrollElement = scrollElementRef.current;

    if (wrapperNode && scrollElement) {
      const { height: wrapperNodeHeight } = wrapperNode.getBoundingClientRect() ?? {};

      const scrollCallback = (e) => {
        const elementScrollTop = e.target.scrollTop ?? e.target.documentElement.scrollTop;
        const newScrollPosition = elementScrollTop + fixedContentSize + safeAreaInset.top;
        const spaceAboveFilters = widgetSize ? widgetSize - (promoSize ?? 0) : 0;
        const wrapperPosition = initialWrapperPosition.current + spaceAboveFilters;

        const hasScrollUp = newScrollPosition < scrollPosition.current;
        const isSticky = newScrollPosition > wrapperPosition;

        if (Math.abs(newScrollPosition - scrollPosition.current) > 1) {
          setIsSticky(() => {
            const wrapperNode = wrapperRef.current;

            if (wrapperNode) {
              if (isSticky && !hasScrollUp) {
                wrapperNode.style.transform = `translateY(-${wrapperNodeHeight}px)`;
                wrapperNode.style.opacity = 0;
              } else {
                wrapperNode.style.transform = `translateY(${isSticky ? -1 : 0}em)`;
                wrapperNode.style.borderBottomLeftRadius = theme.Card.borderRadius;
                wrapperNode.style.borderBottomRightRadius = theme.Card.borderRadius;
                wrapperNode.style.opacity = 1;
              }
            }

            return isSticky;
          });
        }

        scrollPosition.current = newScrollPosition;
        wrapPosition.current = wrapperPosition;
      };

      scrollElement?.addEventListener('scroll', scrollCallback);

      return () => {
        scrollElement?.removeEventListener('scroll', scrollCallback);
      };
    }
  }, [isSticky, fixedContentSize, promoSize, safeAreaInset, widgetSize]);

  const isFromLineUp = useMemo(() => context === StickyMatchActionsRowContexts.LINE_UP, [context]);

  return (
    <>
      {!Target.__MOBILE__ && isSticky ? <Placeholder ref={placeholderRef} /> : null}
      <Wrapper
        className={className}
        ref={measuredWrapperHeight}
        $offsetTop={fixedContentSize}
        $isSticky={isSticky}
        $isMyMatchAvailable={isMyMatchAvailable}
      >
        {isMyMatchAvailable ? <StyledMatchViewModeSwitch $isFromLineUp={isFromLineUp} /> : null}

        {filters?.length > 1 || (isFromLineUp && filters?.length > 0) ? (
          <StyledMatchBetFiltersHandlerContainer
            $marginList={isMyMatchAvailable}
            filters={filters}
            onSelectionChange={onFilterChanged}
            defaultFilterId={defaultFilterId}
            untoggleable={untoggleable}
            noAutoScroll={noAutoScroll}
            context={context}
            $isMyMatchAvailable={isMyMatchAvailable}
            $isSticky={isSticky}
            locationHash={location.hash}
          />
        ) : null}
      </Wrapper>
    </>
  );
};

export default StickyMatchActionsRow;
